// src/components/common/Toast.js
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FiCheckCircle, FiAlertTriangle, FiXCircle, FiInfo, FiX } from 'react-icons/fi';

const toastTypes = {
  success: {
    icon: <FiCheckCircle className="h-6 w-6 text-pastel-green" />,
    style: 'bg-warm-dark/95 backdrop-blur-md border border-pastel-green/30 shadow-glow-sm',
    barStyle: 'bg-pastel-green',
  },
  error: {
    icon: <FiXCircle className="h-6 w-6 text-red-400" />,
    style: 'bg-warm-dark/95 backdrop-blur-md border border-red-500/30 shadow-glow-sm',
    barStyle: 'bg-red-400',
  },
  info: {
    icon: <FiInfo className="h-6 w-6 text-slate-blue" />,
    style: 'bg-warm-dark/95 backdrop-blur-md border border-slate-blue/30 shadow-glow-sm',
    barStyle: 'bg-slate-blue',
  },
  warning: {
    icon: <FiAlertTriangle className="h-6 w-6 text-yellow-400" />,
    style: 'bg-warm-dark/95 backdrop-blur-md border border-yellow-500/30 shadow-glow-sm',
    barStyle: 'bg-yellow-400',
  },
};

function Toast({ message, type = 'info', onDismiss, duration = 5000 }) {
  const [exiting, setExiting] = useState(false);
  const [progress, setProgress] = useState(100);

  const { icon, style, barStyle } = toastTypes[type] || toastTypes.info;

  // Handle auto-dismissal and animation
  useEffect(() => {
    if (duration) {
      let animationTimer = null;
      const exitTimer = setTimeout(() => {
        setExiting(true);
        animationTimer = setTimeout(onDismiss, 400);
      }, duration);

      // Progress bar animation
      const interval = setInterval(() => {
        setProgress(prev => {
          const next = prev - (100 / (duration / 100));
          if (next <= 0) {
            clearInterval(interval);
            return 0;
          }
          return next;
        });
      }, 100);

      return () => {
        clearTimeout(exitTimer);
        if (animationTimer) clearTimeout(animationTimer);
        clearInterval(interval);
      };
    }
  }, [duration, onDismiss]);

  const handleManualDismiss = () => {
    setExiting(true);
    setTimeout(onDismiss, 400);
  };

  // Animation classes
  const animationClass = exiting 
    ? 'animate-toast-out' 
    : 'animate-toast-in';

  return (
    <div
      className={`relative w-full max-w-sm rounded-xl shadow-2xl pointer-events-auto overflow-hidden ${style} ${animationClass}`}
      role="alert"
    >
      <div className="p-4 flex items-start">
        <div className="flex-shrink-0">{icon}</div>
        <div className="ml-3 w-0 flex-1 pt-0.5">
          <p className="text-sm font-medium text-cream">{message}</p>
        </div>
        <div className="ml-4 flex-shrink-0 flex">
          <button
            onClick={handleManualDismiss}
            className="inline-flex text-white/30 rounded-md hover:text-white/70 focus:outline-none transition-colors"
          >
            <span className="sr-only">Close</span>
            <FiX className="h-5 w-5" />
          </button>
        </div>
      </div>
      {/* Timer progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/5">
        <div 
          className={`h-full ${barStyle}`}
          style={{ width: `${progress}%`, transition: 'width 0.1s linear' }}
        ></div>
      </div>
    </div>
  );
}

Toast.propTypes = {
  message: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['success', 'error', 'info', 'warning']),
  onDismiss: PropTypes.func.isRequired,
  duration: PropTypes.number,
};

export default Toast;